'use client';

import { get, kebabCase } from 'lodash-es';
import getQualifiedImageUrl from '../../utils/imageUrlProcessor';
import Typography from '@ui/Typography';
import useShareModalStore from '@/components/store/shareModalSlice';
import Button from '@ui/Button';
import Input from '@ui/Input';
import React, { useState, useEffect } from 'react';
import { MdContentCopy, MdDone } from 'react-icons/md';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { useCreatePostsStore } from '@/utils/CreatePostDialogs';
import { getPostLink } from '@/utils/post.helper';
import { usePathname, useRouter } from 'next/navigation';
import { joinUrl } from '@/utils/stringHelpers';
import useUserProfile from '@/hooks/useUserProfile';
import useHasUserSession from '@/hooks/useHasUserSession';

const iconSize = 65;
const smallIconSize = 40;
const windowSize = {
  width: 1024,
  height: 768,
};

const contributionMessageTemplates = [
  'Just contributed to [Beneficiary Name] and feeling grateful to give back—every bit counts toward a brighter future!',
  'I made a difference today by supporting [Beneficiary Name]. Join me in spreading kindness!',
  'Proud to support [Beneficiary Name]—small acts can lead to big changes.🌍❤️#GiveBack',
  'Helping [Beneficiary Name] with a contribution today! Every step toward change starts with us ⭐#BeTheChange',
  'Feeling grateful to contribute to [Nonprofit Cause] through [Beneficiary Name]. Together, we can make a real impact!',
  'Supporting a cause close to my heart today—[Beneficiary Name] is doing amazing work!💙#MakeADifference',
  'Contributed to [Beneficiary Name] and feeling inspired. If you’re able, let’s create change together!',
  'A little goes a long way! Just gave to [Beneficiary Name] and it feels so good. #GivingBack',
  'Today, I chose kindness. Proud to support [Beneficiary Name] in their incredible work!',
  'Doing my part to support [Beneficiary Name] today! Every contribution counts toward hope and healing. #SpreadGoodness',
];

function ContributionSuccessDialog(props) {
  const { post, reward, message, onClose, user, originPage } = props;

  const badge = get(reward, 'badges.badge', null);
  const pointsEarned = get(reward, 'points.earned', 0);

  const router = useRouter();
  const hasUserSession = useHasUserSession();
  const { beneficiary } = post?.sourcePostData || {};
  const { beneficiaryName, beneficiaryType, relationship } = beneficiary || {};

  const pathName = usePathname();

  const postLink = post ? getPostLink(post) : null;

  const metadata = useShareModalStore((state) => state.metadata);

  const createPostShareLink = (url) => {
    let shareUrl = url || `${window.location.origin}${pathName}`;
    if (!shareUrl.startsWith(window.location.origin)) {
      shareUrl = joinUrl(window.location.origin, url);
    }
    return shareUrl;
  };

  const shareUrl = createPostShareLink(postLink);
  const title = metadata.title || 'Neki';
  const randomMessage = contributionMessageTemplates[
    Math.floor(Math.random() * contributionMessageTemplates.length)
  ].replace(/\[Beneficiary Name\]/g, `${beneficiaryName}`);

  const description = randomMessage;

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 600);
    }
  }, [copied]);

  const handleLinkCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  };

  const isLessThanLargeScreen = useBreakpointHooks((bp) => bp.useSmaller('lg'));
  const isLessThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );
  const isGreaterThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useGreater('md'),
  );

  const { openDialog } = useCreatePostsStore();

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    if (originPage) {
      router.push(originPage);
    }
  };

  const handleCreatePost = () => {
    handleClose();
    setTimeout(() => {
      openDialog('UserPost', description);
    }, 100);
  };

  if (hasUserSession) {
    return (
      <div className="mt-4 flex flex-col items-center">
        <div
          className={`left flex-1/2 flex flex-col items-center justify-center gap-3 ${
            isGreaterThanMediumScreen ? 'mx-[150px]' : ''
          }`}>
          <img
            src={'/assets/images/neki-heart.png'}
            alt="contribution-heart"
            className="h-24 w-24"
          />
          <Typography
            component="div"
            className="text-center text-32"
            variant="h1">
            {user?.first_name || user?.firstName || 'User'}, Thank you so much!
          </Typography>
          {message ? (
            <>
              <Typography
                component="div"
                color="textDisabled"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                {message}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                component="div"
                color="textDisabled"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                Your contribution has been processed successfully. The receipt
                will be sent to your email.
              </Typography>
              <Typography
                component="div"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                Don&apos;t be shy! Share your contribution on Neki and encourage
                others to participate in your community!
              </Typography>
              <Button onClick={handleCreatePost}>
                Create a Post and Share
              </Button>
            </>
          )}
        </div>
        <Typography
          component="div"
          variant="hyperlink"
          className="mb-4 mt-4 cursor-pointer text-center text-15"
          onClick={handleClose}>
          <u>{message ? 'Continue' : 'or Continue...'}</u>
        </Typography>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col items-center">
      <div className={`flex w-full flex-1 flex-col items-center`}>
        <div className="flex-1/2 flex flex-col items-center justify-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={'/assets/images/neki-heart.png'}
            alt="contribution-heart"
            className="h-24 w-24"
          />
          <Typography
            component="div"
            className="text-center text-32"
            variant="h1">
            {user?.first_name || user?.firstName || ''}, Thank you so much!
          </Typography>
          <Typography
            component="div"
            color="textDisabled"
            className="mx-[70px] text-center font-medium"
            variant="h4">
            Your contribution has been processed successfully. The receipt will
            be sent to your email.
          </Typography>
        </div>
        <div
          className={`flex-1/2 flex flex-col items-center justify-center gap-10 ${
            isLessThanLargeScreen ? 'mt-6' : ''
          }`}>
          <Typography
            component="div"
            className={`text-center font-medium ${
              isLessThanLargeScreen ? 'mt-6' : ''
            }`}
            variant="h4">
            Share your contribution on social media and inspire others to
            participate in your cause!
          </Typography>
          <div
            className={`flex flex-wrap items-center justify-start ${
              isLessThanMediumScreen ? 'gap-4' : 'gap-6'
            }`}>
            <EmailShareButton
              url={shareUrl}
              subject={title}
              body={`${description} \n\n`}>
              <EmailIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                windowWidth={windowSize.width}
                windowHeight={windowSize.height}
                round
              />
            </EmailShareButton>

            <WhatsappShareButton
              url={shareUrl}
              title={title}
              separator=" "
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <WhatsappIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </WhatsappShareButton>

            <FacebookShareButton
              url={shareUrl}
              quote={title}
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <FacebookIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </FacebookShareButton>

            <TwitterShareButton
              url={shareUrl}
              title={title}
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <TwitterIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </TwitterShareButton>

            <LinkedinShareButton
              url={shareUrl}
              title={encodeURIComponent(title)}
              summary={encodeURIComponent(description)}
              source="Neki"
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <LinkedinIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </LinkedinShareButton>
          </div>

          <div className="flex w-full items-center justify-between gap-6">
            <div className="flex-1">
              <Input value={shareUrl} disabled />
            </div>
            <Button variant="filled" color="primary" onClick={handleLinkCopy}>
              {!copied ? (
                <MdContentCopy />
              ) : (
                <MdDone className="animate-pulse" />
              )}
            </Button>
          </div>
          {/* <Button className="w-full">Create a Post and Share</Button> */}
        </div>
      </div>
      <Typography
        component="div"
        variant="hyperlink"
        className="mb-4 mt-4 cursor-pointer text-center text-15"
        onClick={handleClose}>
        <u>or Continue...</u>
      </Typography>
    </div>
  );
}

export default ContributionSuccessDialog;
